import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { Switch } from "../components/ui/Switch";
import {
  Plus, Search, Edit, Trash2, Calendar,
  Image as ImageIcon, Link as LinkIcon, BarChart3,
  ArrowUpCircle, Loader2, Monitor, Smartphone, ChevronLeft, ChevronRight
} from "lucide-react";
import { bannerService, Banner } from "../services/banners";
import { toast } from "sonner";
import { getImageUrl } from "../utils";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/AlertDialog";

export function BannerPage() {
  const navigate = useNavigate();
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchBanners = async (page = 1) => {
    setLoading(true);
    try {
      const response = await bannerService.getBanners(page);
      // Laravel pagination returns the array in .data
      setBanners(response.data || []);
      setPagination(response);
    } catch (error) {
      console.error("Erro ao carregar banners:", error);
      toast.error("Não foi possível carregar os banners.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners(currentPage);
  }, [currentPage]);

  const handleToggleActive = async (id: number, currentStatus: boolean) => {
    try {
      await bannerService.toggleBanner(id);
      setBanners(prev => prev.map(b => b.id === id ? { ...b, active: !currentStatus } : b));
      toast.success("Status do banner atualizado!");
    } catch (error) {
      toast.error("Falha ao atualizar status.");
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await bannerService.deleteBanner(id);
      setBanners(prev => prev.filter(b => b.id !== id));
      toast.success("Banner removido com sucesso.");
    } catch (error) {
      toast.error("Erro ao remover banner.");
    }
  };

  const handleMoveToTop = async (id: number) => {
    try {
      await bannerService.moveToTop(id);
      toast.success("Banner movido para o topo!");
      fetchBanners(currentPage);
    } catch (error) {
      toast.error("Erro ao reordenar banner.");
    }
  };

  const filteredBanners = banners.filter(banner => {
    const url = banner.url || "";
    return url.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <main className="flex-1 p-6 bg-gray-50 overflow-auto">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-[#68686A]">Banners</h1>
          <p className="text-[#68686A]">Gerencie banners e campanhas visuais</p>
        </div>
        <Button
          onClick={() => navigate("/novo-banner")}
          className="bg-[#D4C070] hover:bg-[#849D70] text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          Novo Banner
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card className="border border-gray-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-[#68686A]">Total de Banners</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-gray-900">{pagination?.total || 0}</p>
          </CardContent>
        </Card>

        <Card className="border border-gray-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-[#68686A]">Página Atual</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-[#D4C070]">{currentPage} / {pagination?.last_page || 1}</p>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 shadow-sm transition-all duration-200 hover:shadow-md cursor-pointer h-full" onClick={() => fetchBanners(currentPage)}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-[#68686A]">Sincronizar</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-[#849D70]" />
            <span className="text-xs text-gray-500">Clique para atualizar a lista</span>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Buscar por URL..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 border-gray-300"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-dashed border-gray-300">
          <Loader2 className="h-10 w-10 text-[#D4C070] animate-spin mb-4" />
          <p className="text-gray-500 font-medium">Carregando banners...</p>
        </div>
      ) : filteredBanners.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-dashed border-gray-300">
          <p className="text-gray-500 font-medium">Nenhum banner encontrado nesta página.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {filteredBanners.map((banner) => (
              <Card key={banner.id} className="border border-gray-200 hover:shadow-md transition-all overflow-hidden flex flex-col">
                <CardHeader className="pb-3 border-b bg-gray-50/50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`${banner.active ? 'bg-[#849D70]' : 'bg-gray-300'} text-white border-none`}>
                          {banner.active ? "Ativo" : "Inativo"}
                        </Badge>
                        <Badge variant="outline" className="bg-white border-gray-200 text-gray-600">
                          {banner.type === 'desktop' ? <div className="flex items-center gap-1"><Monitor className="h-3 w-3" /> Desktop</div> : <div className="flex items-center gap-1"><Smartphone className="h-3 w-3" /> Mobile</div>}
                        </Badge>
                      </div>
                      <CardTitle className="text-lg font-bold text-gray-800 line-clamp-1">
                        {`Banner #${banner.id} - ${banner.type}`}
                      </CardTitle>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                        <LinkIcon className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate max-w-[250px]">{banner.url || "Sem link"}</span>
                      </div>
                    </div>
                    <Switch
                      checked={banner.active}
                      onCheckedChange={() => handleToggleActive(banner.id!, banner.active)}
                    />
                  </div>
                </CardHeader>

                <CardContent className="pt-4 flex-1">
                  <div className="space-y-4">
                    <div className="relative group aspect-[21/9] overflow-hidden rounded-lg bg-gray-100 border shadow-inner">
                      {banner.image ? (
                        <img
                          src={getImageUrl(banner.image)}
                          alt={`Banner #${banner.id}`}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'https://placehold.co/1200x400?text=Erro+Imagem';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center">
                          <ImageIcon className="h-10 w-10 text-gray-300 mb-2" />
                          <span className="text-xs text-gray-400">Caminho da imagem não definido</span>
                        </div>
                      )}

                      <div className="absolute top-2 right-2 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="secondary" className="h-9 w-9 p-0 rounded-full bg-white shadow-lg text-green-600 hover:scale-110 active:scale-95 transition-all">
                              <ArrowUpCircle className="h-6 w-6" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Mover para o Topo?</AlertDialogTitle>
                              <AlertDialogDescription>
                                O banner "#{banner.id}" será exibido primeiro na loja.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleMoveToTop(banner.id!)} className="bg-[#849D70]">Confirmar</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-4 text-[11px] text-gray-500">
                      <div className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded">
                        <Calendar className="h-3 w-3" />
                        <span>Início: <strong>{banner.starts_at ? new Date(banner.starts_at).toLocaleDateString() : 'Imediato'}</strong></span>
                      </div>
                      <div className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded">
                        <Calendar className="h-3 w-3" />
                        <span>Expira: <strong>{banner.expires_at ? new Date(banner.expires_at).toLocaleDateString() : 'Nunca'}</strong></span>
                      </div>
                      <div className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded">
                        <BarChart3 className="h-3 w-3" />
                        <span>Cliques: <strong>{banner.clicks_count || 0}</strong></span>
                      </div>
                    </div>

                    <div className="flex gap-2 pt-2 mt-auto border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-gray-600 hover:bg-white hover:text-[#D4C070] border-gray-200"
                        onClick={() => navigate(`/editar-banner/${banner.id}`)}
                      >
                        <Edit className="h-3.5 w-3.5 mr-2" />
                        Editar Detalhes
                      </Button>

                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" size="sm" className="text-red-400 hover:text-red-600 border-gray-200">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Excluir Banner?</AlertDialogTitle>
                            <AlertDialogDescription>Esta ação é irreversível.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Voltar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(banner.id!)} className="bg-red-500">Remover</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {pagination && pagination.last_page > 1 && (
            <div className="mt-8 mb-12 flex flex-col items-center gap-3 border-t pt-4">
              <p className="text-sm text-gray-500">
                Mostrando <span className="font-medium">{pagination.from}</span> até{' '}
                <span className="font-medium">{pagination.to}</span> de{' '}
                <span className="font-medium">{pagination.total}</span> banners
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="border-gray-300 text-[#68686A] hover:bg-[#EEF1F2] disabled:opacity-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                  let pageNum;
                  if (pagination.last_page <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= pagination.last_page - 2) {
                    pageNum = pagination.last_page - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCurrentPage(pageNum)}
                      className={
                        currentPage === pageNum
                          ? 'bg-[#D4C070] hover:bg-[#849D70] text-white'
                          : 'border-gray-300 text-[#68686A] hover:bg-[#EEF1F2]'
                      }
                    >
                      {pageNum}
                    </Button>
                  );
                })}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.last_page))}
                  disabled={currentPage === pagination.last_page}
                  className="border-gray-300 text-[#68686A] hover:bg-[#EEF1F2] disabled:opacity-50"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </main>
  );
}

export default BannerPage;
